import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {enqueueSnackbar} from 'notistack'
import {useUpdateMyActivationSettings} from '../../hooks/useActivations.ts'
import {Switch} from '../dialogs/AssignInvitationCodeSteps.tsx'
import {isValidEmail} from './activationPolicy.ts'
import {activationErrorMessage} from './activationErrors.ts'
import IconSpinner from '../../assets/icons/spinner.svg?react'

interface ActivationSettingsCardProps {
    // Current values from GET /agencies/{id}; undefined while loading.
    optInPreTrip?: boolean
    activationReportEmail?: string | null
    contactEmail?: string | null
    onSaved?: (settings: {optInPreTrip: boolean; activationReportEmail: string | null}) => void
}

// Agency users have no settings screen, so the two activation settings live
// here: PATCH /agencies/me/activation-settings (contract §2.2).
export const ActivationSettingsCard: React.FC<ActivationSettingsCardProps> = ({optInPreTrip, activationReportEmail, contactEmail, onSaved}) => {
    const {t} = useTranslation('dashboard')
    const {updateMyActivationSettings, isUpdatingMyActivationSettings} = useUpdateMyActivationSettings()
    const [preTrip, setPreTrip] = useState(!!optInPreTrip)
    const [reportEmail, setReportEmail] = useState(activationReportEmail ?? '')

    useEffect(() => setPreTrip(!!optInPreTrip), [optInPreTrip])
    useEffect(() => setReportEmail(activationReportEmail ?? ''), [activationReportEmail])

    const save = (next: {optInPreTrip: boolean; activationReportEmail: string}) => {
        const settings = {optInPreTrip: next.optInPreTrip, activationReportEmail: next.activationReportEmail.trim() || null}
        return updateMyActivationSettings(settings)
            .then(() => {
                onSaved?.(settings)
                enqueueSnackbar(t('activation.settings.saved'), {variant: 'success'})
            })
            .catch((error) => {
                enqueueSnackbar(activationErrorMessage(error, t), {variant: 'error'})
                throw error
            })
    }

    // The switch saves at once (and rolls back on failure); the email on its button.
    const togglePreTrip = (value: boolean) => {
        setPreTrip(value)
        save({optInPreTrip: value, activationReportEmail: reportEmail}).catch(() => setPreTrip(!value))
    }

    const emailChanged = reportEmail.trim() !== (activationReportEmail ?? '')
    const emailValid = !reportEmail.trim() || isValidEmail(reportEmail)

    return (
        <section aria-labelledby="activation-settings-title" className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm p-[22px] max-[560px]:p-4 flex flex-col gap-4">
            <h2 id="activation-settings-title" className="text-[15px] font-semibold text-neutralBlack-900">{t('activation.settings.sectionTitle')}</h2>

            <div className="flex items-start gap-4 max-[560px]:gap-3">
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                    <span id="activation-pretrip-label" className="text-[13.5px] font-medium text-neutralBlack-900">{t('activation.settings.optInPreTrip')}</span>
                    <span id="activation-pretrip-help" className="text-[12.5px] leading-[1.5] text-neutralBlack-600">{t('activation.settings.optInPreTripHelp')}</span>
                </div>
                <Switch checked={preTrip} onChange={togglePreTrip} label={t('activation.settings.optInPreTrip')}/>
            </div>

            <div className="h-px bg-neutralBlack-100"/>

            <form className="flex flex-col gap-1.5" onSubmit={(e) => {e.preventDefault(); if (emailChanged && emailValid) save({optInPreTrip: preTrip, activationReportEmail: reportEmail}).catch(() => undefined)}}>
                <label htmlFor="activation-report-email" className="text-[13.5px] font-medium text-neutralBlack-900">{t('activation.settings.reportEmail')}</label>
                <span id="activation-report-email-help" className="text-[12.5px] leading-[1.5] text-neutralBlack-600">{t('activation.settings.reportEmailHelp')}</span>
                <div className="flex items-center gap-2.5 flex-wrap mt-1">
                    <input id="activation-report-email" type="email" value={reportEmail}
                           aria-describedby="activation-report-email-help" aria-invalid={!emailValid}
                           placeholder={contactEmail ?? ''}
                           onChange={(e) => setReportEmail(e.target.value)}
                           className="flex-1 min-w-[200px] h-10 px-3 rounded-lg border border-neutralBlack-300 text-sm text-neutralBlack-900 focus:outline-none focus:border-neutralBlack-700"/>
                    <button type="submit" disabled={!emailChanged || !emailValid || isUpdatingMyActivationSettings}
                            className="h-10 px-4 rounded-full bg-neutralBlack-900 text-white text-[13px] font-semibold hover:bg-neutralBlack-700 disabled:bg-neutralBlack-100 disabled:text-neutralBlack-500 disabled:cursor-not-allowed">
                        {isUpdatingMyActivationSettings ? <IconSpinner className="size-4"/> : t('activation.settings.save')}
                    </button>
                </div>
                {!emailValid && <span role="alert" className="text-[12px] text-[#B3261E]">{t('activation.settings.reportEmailInvalid')}</span>}
            </form>
        </section>
    )
}
